import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Clock, LogIn, LogOut, FileSpreadsheet, Loader2, Pencil, Trash2,
  Utensils,
} from 'lucide-react';
import { api, formatDate } from '../lib/api';
import { useAuth } from '../lib/auth';
import { esEquipoNoMedico, isAdmin } from '../lib/roles';
import { PageHeader, SectionCard } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import CuadranteTab from '../components/jornada/CuadranteTab';
import AusenciasTab from '../components/jornada/AusenciasTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const hoyStr = () => new Date().toISOString().slice(0, 10);
const haceUnMes = () => {
  const d = new Date();
  d.setDate(d.getDate() - 31);
  return d.toISOString().slice(0, 10);
};

/** 465 → "7 h 45 min" */
function fmtMinutos(min) {
  if (min === null || min === undefined) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`;
}

// ---------------------------------------------------------------------------
// Tarjeta de fichaje del propio trabajador
// ---------------------------------------------------------------------------

function FicharCard({ user, onFichado }) {
  const [tramosHoy, setTramosHoy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fichando, setFichando] = useState(false);
  const [motivoFichaje, setMotivoFichaje] = useState('jornada');
  // Confirmación con el nombre: en el ordenador compartido de recepción
  // evita que alguien fiche sin querer con la sesión de otro. El backend
  // registra SIEMPRE al usuario de la sesión — no se puede fichar por otro.
  const [confirmOpen, setConfirmOpen] = useState(false);

  const cargarHoy = useCallback(async () => {
    try {
      const res = await api.get('/api/jornada', {
        params: { date_from: hoyStr(), date_to: hoyStr() },
      });
      const items = (res.data.items || []).filter((i) => i.user_id === user?.id);
      setTramosHoy(items.sort((a, b) => (a.entrada || '').localeCompare(b.entrada || '')));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar tu jornada de hoy');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { cargarHoy(); }, [cargarHoy]);

  const abierto = tramosHoy.find((t) => !t.salida);
  const totalHoy = tramosHoy.reduce((acc, t) => acc + (t.minutos || 0), 0);
  const ultimoTramo = tramosHoy[tramosHoy.length - 1];
  const esperandoVueltaComida = !abierto && ultimoTramo?.salida_motivo === 'comida';

  const pedirFichaje = (motivo = 'jornada') => {
    setMotivoFichaje(motivo);
    setConfirmOpen(true);
  };

  const fichar = async () => {
    setConfirmOpen(false);
    setFichando(true);
    try {
      const res = await api.post('/api/jornada/fichar', { motivo: motivoFichaje });
      const { action, record, warning } = res.data;
      toast.success(
        action === 'salida_comida'
          ? `Salida para comer fichada a las ${record.salida}`
          : action === 'vuelta_comida'
            ? `Vuelta de comida fichada a las ${record.entrada}`
            : action === 'entrada'
              ? `Entrada fichada a las ${record.entrada}`
              : `Salida fichada a las ${record.salida}`,
      );
      if (warning) toast.warning(warning);
      await cargarHoy();
      onFichado?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo fichar');
    } finally {
      setFichando(false);
    }
  };

  return (
    <SectionCard title={`Mi jornada de hoy — ${user?.name || ''}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-testid="jornada-hoy">
        <Button
          onClick={() => pedirFichaje(abierto ? 'jornada' : esperandoVueltaComida ? 'vuelta_comida' : 'jornada')}
          disabled={fichando || loading}
          className={abierto
            ? 'bg-red-600 hover:bg-red-700 text-white h-12 px-6 text-base'
            : 'bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6 text-base'}
          data-testid="jornada-fichar"
        >
          {fichando
            ? <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            : abierto ? <LogOut className="w-5 h-5 mr-2" /> : <LogIn className="w-5 h-5 mr-2" />}
          {abierto
            ? `Fichar fin de jornada (entré a las ${abierto.entrada})`
            : esperandoVueltaComida ? 'Fichar vuelta de comida' : 'Fichar entrada'}
        </Button>

        {abierto && (
          <Button
            type="button"
            variant="outline"
            onClick={() => pedirFichaje('comida')}
            disabled={fichando || loading}
            className="h-12 px-5 text-base border-amber-300 text-amber-900 hover:bg-amber-50"
            data-testid="jornada-salir-comida"
          >
            <Utensils className="w-5 h-5 mr-2" /> Salir a comer
          </Button>
        )}

        <div className="flex-1 text-sm text-muted-foreground">
          {loading ? 'Cargando…' : tramosHoy.length === 0 ? (
            'Sin fichajes hoy.'
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {tramosHoy.map((t) => (
                <Badge
                  key={t.id}
                  variant="secondary"
                  className={t.salida
                    ? 'bg-[#2E6E5E]/10 text-[#13231E] border-[#2E6E5E]/30'
                    : 'bg-emerald-100 text-emerald-800 border-emerald-300'}
                >
                  {t.entrada}–{t.salida || 'ahora'}{t.salida_motivo === 'comida' ? ' · comida' : ''}
                </Badge>
              ))}
              <span className="font-medium text-[#13231E]">Total: {fmtMinutos(totalHoy)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 text-xs text-muted-foreground">
        El fichaje queda registrado a nombre de la sesión iniciada ({user?.name}).
        Cada trabajador debe fichar desde su propia cuenta — no se puede fichar por otro.
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="jornada-fichar-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {motivoFichaje === 'comida'
                ? 'Fichar salida para comer'
                : motivoFichaje === 'vuelta_comida'
                  ? 'Fichar vuelta de comida'
                  : abierto ? 'Fichar fin de jornada' : 'Fichar entrada'} como {user?.name}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Vas a fichar con la sesión de <strong>{user?.name}</strong>.
              Si no eres tú, cancela y entra con tu propia cuenta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); fichar(); }}
              className={abierto ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
              data-testid="jornada-fichar-confirmar"
            >
              Sí, soy {user?.name} — fichar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Diálogo de corrección (solo admin/direccion)
// ---------------------------------------------------------------------------

function CorregirDialog({ open, onOpenChange, tramo, onSaved }) {
  const [form, setForm] = useState({ date: '', entrada: '', salida: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !tramo) return;
    setForm({ date: tramo.date || '', entrada: tramo.entrada || '', salida: tramo.salida || '' });
  }, [open, tramo]);

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/jornada/${tramo.id}`, {
        date: form.date,
        entrada: form.entrada,
        salida: form.salida, // "" reabre el tramo
      });
      toast.success('Fichaje corregido');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo corregir el fichaje');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="jornada-corregir-dialog">
        <DialogHeader>
          <DialogTitle>Corregir fichaje — {tramo?.user_name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Fecha</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              className="mt-1.5"
              data-testid="jornada-corregir-fecha"
            />
          </div>
          <div>
            <Label>Entrada</Label>
            <Input
              type="time"
              value={form.entrada}
              onChange={(e) => setForm((f) => ({ ...f, entrada: e.target.value }))}
              className="mt-1.5"
              data-testid="jornada-corregir-entrada"
            />
          </div>
          <div>
            <Label>Salida</Label>
            <Input
              type="time"
              value={form.salida}
              onChange={(e) => setForm((f) => ({ ...f, salida: e.target.value }))}
              className="mt-1.5"
              data-testid="jornada-corregir-salida"
            />
            <div className="text-[11px] text-muted-foreground mt-1">Vacío = tramo abierto</div>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="jornada-corregir-guardar"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function RegistroJornadaPage() {
  const { user } = useAuth();
  // Los administradores NO fichan: solo ven el
  // registro del resto del personal. Los demás solo ven su fichaje.
  const admin = isAdmin(user);
  // Excepción pedida por dirección: su perfil mantiene la tarjeta de fichaje
  // (además del registro completo) para poder probar el flujo sin cambiar
  // de usuario. El resto de administradores no la ven.
  // Los administradores no fichan, salvo el que además pasa consulta. Antes
  // esto estaba atado al correo de una persona concreta; ahora es una marca
  // del usuario que se pone desde Usuarios.
  const puedeFichar = !admin || !!user?.ficha_jornada;
  // Vacaciones, teletrabajo y días libres (administración, 14/10/2026): la
  // pestaña es del equipo no médico, como su servidor
  // (`require_equipo_no_medico`). A quien no lo es (anestesia) no se le
  // enseña una puerta que da 403.
  const verAusencias = esEquipoNoMedico(user);
  const [dateFrom, setDateFrom] = useState(haceUnMes());
  const [dateTo, setDateTo] = useState(hoyStr());
  const [workerId, setWorkerId] = useState('todos');
  const [data, setData] = useState({ items: [], is_management: false, workers: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const cargar = useCallback(async () => {
    if (!admin) return; // el histórico es solo para administradores
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (workerId !== 'todos') params.user_id = workerId;
      const res = await api.get('/api/jornada', { params });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar el registro');
    } finally {
      setLoading(false);
    }
  }, [admin, dateFrom, dateTo, workerId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Total de horas del rango visible (solo tramos cerrados).
  const totalRango = useMemo(
    () => data.items.reduce((acc, t) => acc + (t.minutos || 0), 0),
    [data.items],
  );

  const exportar = () => {
    const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
    if (workerId !== 'todos') params.set('user_id', workerId);
    fetch(`${BACKEND_URL}/api/jornada/export?${params.toString()}`, { credentials: 'include' })
      .then((r) => {
        if (!r.ok) throw new Error('export');
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `registro_jornada_${dateFrom}_${dateTo}.xlsx`;
        a.click();
      })
      .catch(() => toast.error('No se pudo exportar el Excel'));
  };

  const borrar = async (tramo) => {
    if (!window.confirm(`¿Borrar el fichaje de ${tramo.user_name} del ${formatDate(tramo.date)}?`)) return;
    try {
      await api.delete(`/api/jornada/${tramo.id}`);
      toast.success('Fichaje borrado');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo borrar');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Registro de jornada"
        subtitle={admin ? 'Fichajes y cuadrante de todo el equipo' : 'Ficha tu jornada y consulta tus turnos'}
      />

      <Tabs defaultValue="fichajes">
        <TabsList className={`grid w-full ${verAusencias ? 'h-auto max-w-xl grid-cols-3' : 'max-w-sm grid-cols-2'}`}>
          <TabsTrigger value="fichajes" data-testid="jornada-tab-fichajes">Fichajes</TabsTrigger>
          <TabsTrigger value="cuadrante" data-testid="jornada-tab-cuadrante">Cuadrante</TabsTrigger>
          {verAusencias && (
            <TabsTrigger value="ausencias" className="whitespace-normal leading-tight"
              data-testid="jornada-tab-ausencias">
              Vacaciones y teletrabajo
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="fichajes" className="space-y-6 mt-4">

      {/* Los administradores no fichan (salvo dirección, para pruebas) */}
      {puedeFichar && <FicharCard user={user} onFichado={cargar} />}

      {admin && (
      <SectionCard
        title="Histórico"
        action={(
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-[11px]">Desde</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="mt-1 h-9"
                data-testid="jornada-date-from"
              />
            </div>
            <div>
              <Label className="text-[11px]">Hasta</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="mt-1 h-9"
                data-testid="jornada-date-to"
              />
            </div>
            {admin && (
              <div className="min-w-[190px]">
                <Label className="text-[11px]">Trabajador</Label>
                <Select value={workerId} onValueChange={setWorkerId}>
                  <SelectTrigger className="mt-1 h-9" data-testid="jornada-worker-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {data.workers.map((w) => (
                      <SelectItem key={w.user_id} value={w.user_id}>{w.user_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              onClick={exportar}
              className="h-9 bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="jornada-export-excel"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Exportar Excel
            </Button>
          </div>
        )}
      >
        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Cargando registro…</div>
        ) : data.items.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground" data-testid="jornada-vacio">
            <Clock className="w-8 h-8 text-[#2E6E5E] mx-auto mb-2" />
            Sin fichajes en este rango.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="jornada-tabla">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3">Fecha</th>
                  {admin && <th className="py-2 pr-3">Trabajador</th>}
                  <th className="py-2 pr-3">Entrada</th>
                  <th className="py-2 pr-3">Salida</th>
                  <th className="py-2 pr-3">Horas</th>
                  <th className="py-2 pr-3">Corregido</th>
                  {admin && <th className="py-2" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.items.map((t) => (
                  <tr key={t.id} data-testid={`jornada-row-${t.id}`}>
                    <td className="py-2 pr-3 tabular-nums">{formatDate(t.date)}</td>
                    {admin && <td className="py-2 pr-3">{t.user_name}</td>}
                    <td className="py-2 pr-3 tabular-nums">{t.entrada || '—'}</td>
                    <td className="py-2 pr-3 tabular-nums">
                      {t.salida || (
                        <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 border-emerald-300">
                          abierto
                        </Badge>
                      )}
                    </td>
                    <td className="py-2 pr-3 tabular-nums">{fmtMinutos(t.minutos)}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">{t.updated_by_name || ''}</td>
                    {admin && (
                      <td className="py-2 text-right whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing(t)}
                          data-testid={`jornada-editar-${t.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => borrar(t)}
                          data-testid={`jornada-borrar-${t.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border font-medium text-[#13231E]">
                  <td className="py-2 pr-3" colSpan={4}>Total del rango (tramos cerrados)</td>
                  <td className="py-2 pr-3 tabular-nums">{fmtMinutos(totalRango)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </SectionCard>
      )}

      <CorregirDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        tramo={editing}
        onSaved={cargar}
      />

        </TabsContent>

        <TabsContent value="cuadrante" className="mt-4">
          <CuadranteTab user={user} admin={admin} />
        </TabsContent>

        {/* Vacaciones, teletrabajo y días libres del equipo no médico
            (administración, 14/10/2026): calendario anual de todos, días a
            mano, rangos y patrones. Ver components/jornada/AusenciasTab.jsx. */}
        {verAusencias && (
          <TabsContent value="ausencias" className="mt-4">
            <AusenciasTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
